import {
  Box,
  Button,
  Divider,
  Flex,
  Icon,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Tooltip,
  useTheme,
} from '@chakra-ui/react';

import { useCallback, useState } from 'react';
import { SortableList } from './sortablednd';
import { countCategory, eliminateHtmlTags } from '../shared/utils';
import { ErasorIcon, InfoIcon, SearchIcon } from '../icons';
import { useGlobalState } from '../hooks/useGlobalState';
import { getUser } from './api/endpoints';
import { useToastHook } from '../appToast';
import Loader from '../appLoader';

interface metricListType {
  metricKey: string;
  metricName: string;
  tabs: string[] | null;
  tooltip?: string;
  id?: string;
}
interface MetricState {
  metricTitle: string;
  metricKey: string;
  sequence: number;
  id?: string;
}
export function ConfigureColumns({
  isOpen,
  onClose,
  metricList,
  permanentColumns,
  defaultMetrics,
  selectedMetrics,
  setSelectedMetrics,
  componentOrigin,
  selectedPermanentColumns,
  setSelectedPermanentColumns,
  team,
  saveColumns,
  saveColumnsIsLoading,
}: {
  componentOrigin: string;
  metricList: metricListType[];
  permanentColumns: string[];
  defaultMetrics: string[];
  isOpen: boolean;
  selectedMetrics: MetricState[];
  setSelectedMetrics: (selectedMetrics: MetricState[]) => void;
  onClose: () => void;
  onOpen: () => void;
  selectedPermanentColumns: string[];
  setSelectedPermanentColumns: (selectedPermanentColumns: string[]) => void;
  saveColumns: (
    variables: any,
    options?: {
      onSuccess?: (data: any) => void;
      onError?: (error: unknown) => void;
    }
  ) => void;
  saveColumnsIsLoading: boolean;
  team?: string;
}) {
  const { colors } = useTheme();
  const { globalState, setGlobalState } = useGlobalState();
  const [tabIndex, setTabIndex] = useState(0);
  const [getUserLoading, setGetUserLoading] = useState(false);
  const [newToast] = useToastHook();
  const [searchTerm, setSearchTerm] = useState('');
  const [permanentItems, setPermanentItems] = useState<string[]>(
    selectedPermanentColumns
  );
  const [items, setItems] = useState<any[]>(
    selectedMetrics.map((selectedMetric) =>
      selectedMetric.metricKey === 'CUSTOM'
        ? selectedMetric.metricTitle
        : selectedMetric?.id ?? selectedMetric.metricKey
    )
  );

  const handleRemoveMetric = useCallback((metric: any) => {
    setItems((prev) => prev.filter((item) => item !== metric));
  }, []);

  const tabs: string[] = [
    'All',
    ...new Set(
      metricList
        .flatMap((item: metricListType) => item.tabs || [])
        .filter(Boolean)
    ),
  ] as string[];

  const handleTogglePermanentColumn = useCallback((column: string) => {
    setPermanentItems((prev) =>
      prev.includes(column)
        ? prev.filter((col) => col !== column)
        : [...prev, column]
    );
  }, []);

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent maxW={700} h={657} borderRadius={16} overflow={'hidden'}>
        <ModalHeader fontSize={'md'} px={4} pt={4} pb={0}>
          Configure columns
        </ModalHeader>
        <Divider w={'95%'} mx="auto" my={4} />
        <ModalCloseButton />
        <ModalBody
          mx={4}
          py={0}
          px={0}
          border={'1px solid #E4E4E4'}
          borderRadius={8}
          display={'grid'}
          gridTemplateColumns={'repeat(2, 1fr)'}
          bg={'#FBFBFB'}
          gap={1}
          overflow={'hidden'}
        >
          {(saveColumnsIsLoading || getUserLoading) && <Loader />}
          <Box py={5} pl={3} pr={1}>
            <Text fontSize={'sm'} fontWeight={'semibold'} mb={4}>
              Visible columns ({items.length + (permanentItems?.length ?? 0)}) -
              Drag to reorder
            </Text>

            <Stack spacing={'7px'} px={1} overflow={'auto'} h={440} py={1}>
              <SortableList
                permanentColumns={permanentColumns}
                metricList={metricList}
                items={items}
                setItems={setItems}
                handleRemoveMetric={handleRemoveMetric}
                permanentItems={permanentItems}
                handleTogglePermanentColumn={handleTogglePermanentColumn}
                componentOrigin={componentOrigin}
              />
            </Stack>
          </Box>
          <Box bg={'white'} borderRadius={8} p={4} m={2} pt={3} pb={0}>
            <Box>
              <Text fontSize={'sm'} fontWeight={'semibold'}>
                Available Columns
              </Text>
              <Box mt={2} mb={3}>
                <Flex justify={'center'} align={'center'} position={'relative'}>
                  <Icon
                    position={'absolute'}
                    zIndex={10}
                    left={2}
                    bottom={'11px'}
                  >
                    <SearchIcon
                      height={20}
                      width={20}
                      color={colors.gray['300']}
                    />
                  </Icon>
                  <Input
                    id="searchValue"
                    name="searchValue"
                    borderRightRadius={0}
                    placeholder={'Search Metrics'}
                    pl={7}
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(eliminateHtmlTags(e.target.value));
                    }}
                  />
                </Flex>
              </Box>
            </Box>
            <Box>
              <Tabs
                index={tabIndex}
                onChange={(index) => setTabIndex(index)}
                w={'full'}
                variant="unstyled"
                size={'sm'}
                colorScheme="twitter"
              >
                <Text
                  color="text.secondary2"
                  fontSize={'sm'}
                  fontWeight={'semibold'}
                >
                  Categories
                </Text>
                <TabList
                  w={'full'}
                  display={'flex'}
                  flexWrap={'wrap'}
                  gap={2}
                  mt={2}
                  mb={4}
                >
                  {tabs.map((tab: string) => {
                    return (
                      <Tab
                        key={tab}
                        _selected={{
                          border: '1px solid',
                          borderColor: 'primary',
                          color: 'primary',
                        }}
                        py={1}
                        px={2}
                        borderRadius={2}
                        fontSize={'xs'}
                        fontWeight={'medium'}
                        border={'1px solid #E0E0E0'}
                        color={'text.secondary2'}
                        bg={'#FBFBFB'}
                      >
                        {tab}:{' '}
                        {tab === 'All'
                          ? metricList.length
                          : countCategory(metricList, tab)}
                      </Tab>
                    );
                  })}
                </TabList>
                <Divider />
                <Flex justify={'space-between'} mr={'10px'} mb={1}>
                  <Text
                    color={'text.secondary2'}
                    fontSize={'sm'}
                    fontWeight={'semibold'}
                    my={2}
                  >
                    {`${tabs[tabIndex]} ${
                      tabs[tabIndex] === 'Others' ? '' : 'Metrics'
                    }`}
                  </Text>
                </Flex>

                <TabPanels
                  style={{
                    height: `180px`,
                    maxHeight: `200px`,
                    overflow: 'auto',
                    marginBottom: '2px',
                  }}
                >
                  {tabs.map((tab: string) => (
                    <TabPanel key={tab} pt={0} px={0}>
                      {tab === 'All'
                        ? metricList
                            ?.filter(
                              (metric: metricListType) =>
                                (searchTerm.trim() === '' ||
                                  metric.metricName
                                    ?.toLocaleLowerCase()
                                    ?.includes(searchTerm.toLowerCase())) &&
                                !items.includes(
                                  metric.metricKey === 'CUSTOM'
                                    ? metric.metricName
                                    : metric?.id ?? metric.metricKey
                                )
                            )
                            .map((item: any) => (
                              <Flex
                                align={'center'}
                                gap={1}
                                key={
                                  item?.metricKey === 'CUSTOM'
                                    ? item?.metricName
                                    : item?.id ?? item?.metricKey
                                }
                                py={1}
                              >
                                {item?.tooltip && (
                                  <Tooltip label={item?.tooltip} hasArrow>
                                    <Box>
                                      <InfoIcon width={17} height={17} />
                                    </Box>
                                  </Tooltip>
                                )}
                                <Text
                                  fontSize="sm"
                                  fontWeight="normal"
                                  fontFamily="heading"
                                  w="fit-content"
                                  cursor={'pointer'}
                                  _hover={{ color: 'text.secondary3' }}
                                  onClick={() => {
                                    const itemToAdd =
                                      item.metricKey === 'CUSTOM'
                                        ? item.metricName
                                        : item?.id ?? item.metricKey;

                                    if (!items.includes(itemToAdd)) {
                                      setItems((prev: any) => {
                                        return [...prev, itemToAdd];
                                      });
                                    }
                                  }}
                                >
                                  {item.metricName}
                                </Text>
                              </Flex>
                            ))
                        : metricList
                            ?.filter(
                              (metric: metricListType) =>
                                metric.tabs?.includes(tab) &&
                                (searchTerm.trim() === '' ||
                                  metric.metricName
                                    ?.toLocaleLowerCase()
                                    ?.includes(searchTerm.toLowerCase())) &&
                                !items.includes(
                                  metric.metricKey === 'CUSTOM'
                                    ? metric.metricName
                                    : metric?.id ?? metric.metricKey
                                )
                            )
                            .map((item: any) => (
                              <Flex
                                align={'center'}
                                gap={1}
                                key={
                                  item?.metricKey === 'CUSTOM'
                                    ? item?.metricName
                                    : item?.id ?? item?.metricKey
                                }
                                py={1}
                              >
                                {item?.tooltip && (
                                  <Tooltip label={item?.tooltip} hasArrow>
                                    <Box>
                                      <InfoIcon width={17} height={17} />
                                    </Box>
                                  </Tooltip>
                                )}
                                <Text
                                  fontSize="sm"
                                  fontWeight="normal"
                                  fontFamily="heading"
                                  cursor={'pointer'}
                                  w="fit-content"
                                  _hover={{ color: 'text.secondary3' }}
                                  onClick={() => {
                                    const itemToAdd =
                                      item.metricKey === 'CUSTOM'
                                        ? item.metricName
                                        : item?.id ?? item.metricKey;

                                    if (!items.includes(itemToAdd)) {
                                      setItems((prev: any) => {
                                        return [...prev, itemToAdd];
                                      });
                                    }
                                  }}
                                >
                                  {item.metricName}
                                </Text>
                              </Flex>
                            ))}
                    </TabPanel>
                  ))}
                </TabPanels>
              </Tabs>
            </Box>
          </Box>
        </ModalBody>
        <Divider w={'95%'} mx="auto" my={3} />

        <ModalFooter px={4} pt={0} pb={4}>
          <Button
            fontWeight={'normal'}
            fontSize={'sm'}
            mr={3}
            variant={'unstyled'}
            display={'flex'}
            _hover={{ bg: '#FBFBFB' }}
            gap={1}
            px={1}
            borderRadius={2}
            onClick={() => {
              setItems(defaultMetrics);
              setPermanentItems(permanentColumns);
            }}
          >
            <ErasorIcon />
            Reset to default
          </Button>
          <Button
            size={'sm'}
            variant="filled"
            onClick={() => {
              saveColumns(
                { metrics: [...items, ...permanentItems], team },
                {
                  onSuccess: () => {
                    if (
                      componentOrigin !== 'contributor-table' &&
                      componentOrigin !== 'team-table'
                    ) {
                      setSelectedMetrics(
                        items.map((metric: string, index: number) => {
                          return {
                            sequence: index + 1,
                            metricKey: metric,
                            metricTitle:
                              metricList.find(
                                (metricItem: metricListType) =>
                                  metricItem.metricKey === metric
                              )?.metricName ?? '',
                          };
                        })
                      );

                      setSelectedPermanentColumns(permanentItems);
                      onClose();
                      return;
                    }
                    setGetUserLoading(true);
                    getUser()
                      .then((res) => {
                        setGlobalState({ ...globalState, dto: res.dto });
                        setGetUserLoading(false);
                        if (componentOrigin === 'team-table') {
                          setSelectedMetrics(
                            items.map((item: string, index: number) => {
                              const metricItem = metricList.find(
                                (metricListItem: metricListType) =>
                                  metricListItem.metricKey === 'CUSTOM'
                                    ? metricListItem.metricName === item
                                    : metricListItem.id === item ||
                                      metricListItem.metricKey === item
                              );

                              return {
                                sequence: index + 1,
                                metricKey: metricItem?.metricKey ?? item,
                                metricTitle: metricItem?.metricName ?? item,
                                id: metricItem?.id ?? metricItem?.metricKey,
                              };
                            })
                          );
                        } else {
                          setSelectedMetrics(
                            items.map((metric: string, index: number) => {
                              const metricItem = metricList.find(
                                (metricItem: metricListType) =>
                                  metricItem.metricKey === metric ||
                                  metricItem.id === metric
                              );
                              return {
                                sequence: index + 1,
                                metricKey: metricItem?.metricKey ?? metric,
                                metricTitle: metricItem?.metricName ?? metric,
                              };
                            })
                          );
                        }

                        setSelectedPermanentColumns(permanentItems);
                        onClose();
                      })
                      .catch((err) => {
                        setGetUserLoading(false);
                        newToast({
                          message: err?.message ?? 'Something went wrong',
                          status: 'error',
                        });
                      });
                  },
                  onError: () => {
                    newToast({
                      status: 'error',
                      message: 'Failed to add to Metrics',
                    });
                  },
                }
              );
            }}
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default ConfigureColumns;
